import type { CSSProperties } from 'react';
import type { TableDefinition } from '../engine/tables/definition';

export const THEME_ART_ATLAS_URL = '/theme-art/theme-art-atlas.webp';
export const PIN_BACKBOX_URL = '/brand/pin-showcase.webp';
export const THEME_ART_FRAME_COUNT = 18;

export type ThemeMachineArtId =
  | 'candy-carnival'
  | 'cosmic-relay'
  | 'haunted-keep'
  | 'neon-rush'
  | 'jungle-temple'
  | 'deep-sea-dive'
  | 'clockwork-district'
  | 'frontier-run'
  | 'dragon-keep';

export interface ThemeMachineArt {
  id: ThemeMachineArtId;
  backglassFrame: number;
  topperFrame: number;
}

export interface MachineBackboxTextureSpec {
  url: string;
  offsetY: number;
  repeatY: number;
}

const ART: Record<ThemeMachineArtId, ThemeMachineArt> = {
  'cosmic-relay': { id: 'cosmic-relay', backglassFrame: 0, topperFrame: 1 },
  'haunted-keep': { id: 'haunted-keep', backglassFrame: 2, topperFrame: 3 },
  'neon-rush': { id: 'neon-rush', backglassFrame: 4, topperFrame: 5 },
  'candy-carnival': { id: 'candy-carnival', backglassFrame: 6, topperFrame: 7 },
  'jungle-temple': { id: 'jungle-temple', backglassFrame: 8, topperFrame: 9 },
  'deep-sea-dive': { id: 'deep-sea-dive', backglassFrame: 10, topperFrame: 11 },
  'clockwork-district': { id: 'clockwork-district', backglassFrame: 12, topperFrame: 13 },
  'frontier-run': { id: 'frontier-run', backglassFrame: 14, topperFrame: 15 },
  'dragon-keep': { id: 'dragon-keep', backglassFrame: 16, topperFrame: 17 },
};

export function machineArtById(id: string | undefined): ThemeMachineArt | undefined {
  return id && id in ART ? ART[id as ThemeMachineArtId] : undefined;
}

export function machineArtForTable(table: TableDefinition): ThemeMachineArt | undefined {
  return machineArtById(table.theme.machineArtId);
}

export function themeArtSpriteStyle(frame: number): CSSProperties {
  const safeFrame = Math.max(0, Math.min(THEME_ART_FRAME_COUNT - 1, Math.round(frame)));
  const position = THEME_ART_FRAME_COUNT <= 1 ? 0 : (safeFrame / (THEME_ART_FRAME_COUNT - 1)) * 100;
  return {
    backgroundImage: `url(${THEME_ART_ATLAS_URL})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `100% ${THEME_ART_FRAME_COUNT * 100}%`,
    backgroundPosition: `center ${position}%`,
  };
}

export function themeArtUv(frame: number): { offsetY: number; repeatY: number } {
  const safeFrame = Math.max(0, Math.min(THEME_ART_FRAME_COUNT - 1, Math.round(frame)));
  const repeatY = 1 / THEME_ART_FRAME_COUNT;
  return { offsetY: 1 - (safeFrame + 1) * repeatY, repeatY };
}

export function machineBackboxTextureForTable(table: TableDefinition): MachineBackboxTextureSpec {
  const art = machineArtForTable(table);
  if (!art) return { url: PIN_BACKBOX_URL, offsetY: 0, repeatY: 1 };
  return { url: THEME_ART_ATLAS_URL, ...themeArtUv(art.topperFrame) };
}

export const THEME_MACHINE_ART = Object.values(ART);
